/**
 * Textos de "Simular venda" e do "Cenário do PCP", em português de
 * marcenaria. Puro (sem React): as regras ficam em `utils/capacidade`
 * (simulacao.js, reprogramacao.js); aqui só se decide como cada resultado é
 * dito, na tela e no arquivo exportado.
 */
import {
  AMOSTRA_MINIMA_FLUXO,
  CAMADAS_DA_FILA,
  INDISPONIVEL,
  MOTIVO_SIMULACAO,
  STATUS_CONFERENCIA,
  TIPO_INSTRUCAO,
  VEREDITO,
} from "../../utils/capacidade";
import { plural } from "../../utils/dinheiro";
import { dataCompleta, ddmm } from "./apresentacao";

export const VEREDITO_NA_TELA = Object.freeze({
  [VEREDITO.CABE]: { frase: "Cabe com folga", tom: "success" },
  [VEREDITO.FALAR_COM_PCP]: { frase: "Falar com o PCP", tom: "warning" },
  [VEREDITO.NAO_CABE]: { frase: "Não cabe", tom: "danger" },
});

export const CLASSES_NA_TELA = Object.freeze([
  { id: "leve", rotulo: "Leve" },
  { id: "normal", rotulo: "Normal" },
  { id: "pesado", rotulo: "Pesado" },
]);

/** "3 ambientes (1 pesado) para 30/06/2026". */
export function textoDaEntrada({ ambientes, entregaDesejada }) {
  const fora = CLASSES_NA_TELA.filter((c) => c.id !== "normal")
    .map((c) => ({ ...c, n: ambientes.filter((a) => a.classe === c.id).length }))
    .filter((c) => c.n > 0)
    .map((c) => `${c.n} ${c.n === 1 ? c.rotulo.toLowerCase() : `${c.rotulo.toLowerCase()}s`}`);
  const classes = fora.length ? ` (${fora.join(", ")})` : "";
  return `${plural(ambientes.length, "ambiente", "ambientes")}${classes} para ${dataCompleta(entregaDesejada)}`;
}

function textoDosEmpurrados(n) {
  return n === 1
    ? "1 cliente que hoje está no prazo passaria a atrasar no pior caso."
    : `${n} clientes que hoje estão no prazo passariam a atrasar no pior caso.`;
}

/** Uma frase por motivo do veredito. */
export function textoDoMotivo(codigo, resultado) {
  const { termino, alvo } = resultado.capacidade;
  const datas = resultado.fluxo?.seHojeAssinar || {};
  switch (codigo) {
    case MOTIVO_SIMULACAO.TERMINA_COM_FOLGA:
      return `Mesmo no pior caso a fila abre vaga para a venda até ${ddmm(termino.max)}; o limite é ${ddmm(alvo)}.`;
    case MOTIVO_SIMULACAO.TERMINA_NO_LIMITE:
      return (
        `A fila abre vaga para a venda entre ${ddmm(termino.min)} e ${ddmm(termino.max)} e ela precisa terminar até ` +
        `${ddmm(alvo)}: está dentro da margem de erro da fila.`
      );
    case MOTIVO_SIMULACAO.TERMINA_DEPOIS:
      return `Mesmo no melhor caso a fila só abre vaga para a venda em ${ddmm(termino.min)}, depois do limite de ${ddmm(alvo)}.`;
    case MOTIVO_SIMULACAO.EMPURRA_CLIENTES:
      return `Para encaixar a venda, ${textoDosEmpurrados(resultado.empurradosPiorCaso.length).replace(/^./, (c) => c.toLowerCase())}`;
    case MOTIVO_SIMULACAO.PRAZO_SEGURO:
      return `Entrega no prazo seguro: 3 em cada 4 ambientes foram da venda ao carregamento em até ${resultado.fluxo.p75} dias úteis (${ddmm(datas.p75)} se assinar hoje).`;
    case MOTIVO_SIMULACAO.PRAZO_APERTADO:
      return `Entrega antes do prazo seguro (${ddmm(datas.p75)}): só uma parte dos ambientes foi tão rápido da venda ao carregamento.`;
    case MOTIVO_SIMULACAO.PRAZO_IMPROVAVEL:
      return (
        `Prazo improvável, independente da capacidade: só 1 em cada 4 ambientes foi da venda ao carregamento em até ` +
        `${resultado.fluxo.p25} dias úteis (${ddmm(datas.p25)} se assinar hoje).`
      );
    case MOTIVO_SIMULACAO.FLUXO_SEM_DADOS:
      return (
        `O prazo de fluxo não opina: só ${plural(resultado.fluxo?.n || 0, "ambiente carregado", "ambientes carregados")} ` +
        `nas últimas 52 semanas (o mínimo é ${AMOSTRA_MINIMA_FLUXO}).`
      );
    default:
      return codigo;
  }
}

/**
 * A mesma venda na leitura com as exceções marcadas (leve 0,5 · pesado 2),
 * enquanto ela não é a conta oficial. `null` sem segunda leitura.
 */
export function textoSegundaLeituraDaSimulacao(resultado) {
  const segunda = resultado?.segundaLeitura;
  if (!segunda) return null;
  const { termino, alvo, cobertura } = segunda;
  const frase = VEREDITO_NA_TELA[segunda.veredito].frase;
  const pct = Math.round((cobertura?.fracao || 0) * 100);
  return (
    `Contando leve como meio ambiente e pesado como dois, a marcenaria terminaria entre ${ddmm(termino.min)} e ` +
    `${ddmm(termino.max)} (limite ${ddmm(alvo)}). Nessa conta, pela capacidade: ${frase}. É uma segunda ` +
    `leitura: só ${pct}% dos terminados usados no ritmo têm marca do PCP, e o veredito acima segue a contagem ` +
    "de ambientes."
  );
}

/** Por que a simulação ou o cenário não dão resposta. */
export function textoIndisponivel(resultado) {
  if (resultado.motivo === INDISPONIVEL.CALENDARIO_AUSENTE) {
    return (
      `Falta cadastrar os feriados e recessos de ${(resultado.anos || []).join(", ")} em Configurações > Calendário: ` +
      "sem eles não dá para contar os dias úteis até a entrega."
    );
  }
  return "Sem ritmo medido não há veredito (veja o aviso no topo da página). A página não inventa prazo.";
}

/** "o que esse prazo de fluxo significa", para a régua do bloco. */
export function linhasDoFluxo(fluxo) {
  if (!fluxo?.suficiente) return [];
  const { p25, p50, p75, seHojeAssinar } = fluxo;
  return [
    { id: "p25", rotulo: "Rápido (1 em 4)", du: p25, data: seHojeAssinar.p25 },
    { id: "p50", rotulo: "Metade", du: p50, data: seHojeAssinar.p50 },
    { id: "p75", rotulo: "Prazo seguro (3 em 4)", du: p75, data: seHojeAssinar.p75, destaque: true },
  ];
}

export function textoOcupacaoCurta(ocupacao) {
  if (!ocupacao) return "—";
  if (ocupacao.semDiaUtil) return "sem dia útil";
  return ocupacao.texto;
}

/** Só os períodos em que a mudança mexe, mais o primeiro (referência). */
export function periodosQueMudam(ocupacao) {
  return (ocupacao || []).filter(
    (linha, i) => i === 0 || textoOcupacaoCurta(linha.antes) !== textoOcupacaoCurta(linha.depois),
  );
}

export function textoEmpurrado(e) {
  return `entrega ${ddmm(e.entrega)}: saía por volta de ${ddmm(e.antes)}, passa para ${ddmm(e.depois)}`;
}

// ------------------------------------------------------------ cenário do PCP

/**
 * Pedidos da fila para o seletor do Cenário do PCP: `[{chave, pedido,
 * cliente, ambientes, entrega, atrasado}]`, atrasados primeiro, depois pela
 * entrega mais próxima.
 */
export function pedidosDaFila(painel, hoje) {
  const grupos = new Map();
  (painel?.abertos || [])
    .filter((item) => CAMADAS_DA_FILA.includes(item.camada) && item.pedido)
    .forEach((item) => {
      const chave = `${item.pedido}|${item.cliente || ""}`;
      const grupo = grupos.get(chave) || { chave, pedido: item.pedido, cliente: item.cliente, ambientes: 0, entrega: null };
      grupo.ambientes += 1;
      if (item.entrega && (!grupo.entrega || item.entrega < grupo.entrega)) grupo.entrega = item.entrega;
      grupos.set(chave, grupo);
    });
  return [...grupos.values()]
    .map((g) => ({ ...g, atrasado: Boolean(g.entrega && g.entrega < hoje) }))
    .sort((a, b) => {
      if (a.atrasado !== b.atrasado) return a.atrasado ? -1 : 1;
      if (a.entrega === b.entrega) return String(a.pedido).localeCompare(String(b.pedido), "pt-BR", { numeric: true });
      if (!a.entrega) return 1;
      if (!b.entrega) return -1;
      return a.entrega < b.entrega ? -1 : 1;
    });
}

export function rotuloDoPedido(p) {
  const entrega = p.entrega ? `entrega ${ddmm(p.entrega)}` : "sem entrega";
  return `${p.pedido} · ${p.cliente || "—"} (${plural(p.ambientes, "ambiente", "ambientes")}, ${entrega})`;
}

/** Linhas para copiar e colar: "Linha 57 · COZINHA: digite …". */
export function textoParaCopiar(itens) {
  return itens.map((item) => `Linha ${item.linha_planilha} · ${item.ambiente}: ${item.instrucao.texto}`).join("\n");
}

const ROTULO_COLUNA = Object.freeze({
  entrega_contrato: "N",
  dias_reprogramado: "CQ",
  data_reprogramada: "CR",
});

function valorDaMarca(campo, valor) {
  if (valor === null || valor === undefined) return "vazio";
  return campo === "dias_reprogramado" ? String(valor) : dataCompleta(valor);
}

/** `{texto, tom}` da conferência de uma marca. */
export function estadoDaMarca(marca) {
  const esperado = `${ROTULO_COLUNA[marca.campo]} = ${valorDaMarca(marca.campo, marca.esperado)}`;
  if (marca.status === STATUS_CONFERENCIA.APLICADO) return { esperado, texto: "aplicado", tom: "success" };
  if (marca.status === STATUS_CONFERENCIA.DIVERGIU) {
    const n = Math.abs(marca.diferenca);
    const sentido = marca.diferenca > 0 ? "depois" : "antes";
    return {
      esperado,
      texto: `divergiu ${plural(n, "dia útil", "dias úteis")} (${sentido}; está ${valorDaMarca(marca.campo, marca.atual)})`,
      tom: "warning",
    };
  }
  return { esperado, texto: "pendente", tom: "neutral" };
}

export function podeMarcar(instrucao) {
  return Boolean(instrucao?.campo) && instrucao.tipo !== TIPO_INSTRUCAO.FORMULA_VAZIA;
}

// ------------------------------------------------------------ exportação

/** Seção "Simulação" do arquivo exportado (só quando há uma ativa). */
export function secaoDaSimulacao({ entrada, resultado }) {
  const linhas = [{ item: "Simulação", valor: textoDaEntrada(entrada) }];
  // Entrada recusada (ex.: link aberto depois da data simulada): a tela já
  // avisa; o arquivo leva o mesmo aviso em vez de um veredito que não existe.
  if (resultado?.erroEntrada) {
    linhas.push({ item: "Resultado", valor: resultado.erroEntrada });
  } else if (resultado?.indisponivel) {
    linhas.push({ item: "Resultado", valor: textoIndisponivel(resultado) });
  } else if (resultado) {
    const { termino, alvo, posicoes } = resultado.capacidade;
    linhas.push(
      { item: "Veredito", valor: VEREDITO_NA_TELA[resultado.veredito].frase },
      ...resultado.motivos.map((m, i) => ({ item: `Motivo ${i + 1}`, valor: textoDoMotivo(m.codigo, resultado) })),
      { item: "Posição na fila", valor: `${posicoes.primeira} a ${posicoes.ultima}` },
      {
        item: "Marcenaria termina",
        valor: `entre ${dataCompleta(termino.min)} e ${dataCompleta(termino.max)} (mais provável ${dataCompleta(termino.central)}); limite ${dataCompleta(alvo)}`,
      },
      ...(textoSegundaLeituraDaSimulacao(resultado)
        ? [{ item: "Com as exceções marcadas", valor: textoSegundaLeituraDaSimulacao(resultado) }]
        : []),
      ...linhasDoFluxo(resultado.fluxo).map((l) => ({
        item: `Prazo de fluxo: ${l.rotulo}`,
        valor: `${l.du} dias úteis (${dataCompleta(l.data)} se assinar hoje)`,
      })),
      ...resultado.empurrados.map((e) => ({
        item: `Empurrado: ${[e.pedido, e.cliente].filter(Boolean).join(" · ")}${e.vendaAssinadaId ? " (venda assinada)" : ""}`,
        valor: textoEmpurrado(e),
      })),
      ...periodosQueMudam(resultado.ocupacao).map((o) => ({
        item: `Ocupação ${o.periodo.rotulo}`,
        valor: `${textoOcupacaoCurta(o.antes)} → ${textoOcupacaoCurta(o.depois)}`,
      })),
    );
  }
  return {
    id: "simulacao",
    label: "Simulação",
    columns: [
      { key: "item", label: "Item", type: "text" },
      { key: "valor", label: "Valor", type: "text" },
    ],
    rows: linhas,
  };
}
